import React, { useEffect, useState } from 'react';
import { Table, Button, Stack, Divider, SelectPicker, Checkbox, IconButton, Message } from 'rsuite';
import EditIcon from '@rsuite/icons/Edit';
import { AxiosError } from 'axios';
import { Link } from 'react-router-dom';
import PageContent from '@/components/PageContent';
import TablePagination from '@/components/TablePagination';
import { BaseErrorModel, PaginationFilterResult } from '@/models/core.model';
import {
  EnergyHubClubVisit, EnergyHubClubVisitPaginationFilter, EnergyHubClubVisitStatus, EnergyHubClubVisitStatusList,
  energyHubTimeZone,
} from '@/models/energy-hub.model';
import { getPagedAllEnergyHubClubVisits, getPagedEnergyHubClubVisits } from '@/services/energy-hub.service';
import { toastErrorMessage } from '@/utils/toasterMessage';
import { jsDateToZone } from '@/utils/date-time';
import { EnergyHubClubVisitStatusTag } from '../list/energy-hub-status-tag';
import ClubVisitDrawer from './club-visit-drawer';

const { Column, HeaderCell, Cell } = Table;

interface Props {
  /** Omit for Hub Runs across every hub (the request queue). */
  energyHubId?: number;
}

const EnergyHubClubVisitList: React.FC<Props> = ({ energyHubId }) => {
  const allHubs = energyHubId == null;
  // The queue opens on club requests waiting for review; a hub opens on its upcoming runs.
  const [filterReq, setFilterReq] = useState<EnergyHubClubVisitPaginationFilter>(allHubs
    ? { ...new EnergyHubClubVisitPaginationFilter(), status: EnergyHubClubVisitStatus.Requested }
    : { ...new EnergyHubClubVisitPaginationFilter(), upcomingOnly: true });
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());
  const [items, setItems] = useState<EnergyHubClubVisit[]>([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<EnergyHubClubVisit | undefined>(undefined);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    fetchData();
  }, [filterReq, energyHubId, reloadKey]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const result = allHubs
        ? await getPagedAllEnergyHubClubVisits(filterReq)
        : await getPagedEnergyHubClubVisits(energyHubId, filterReq);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({ ...pre, pageNumber: result.pageNumber, totalRecords: result.totalRecords, pageSize: result.pageSize }));
      }
    } catch (err) {
      setItems([]);
      const em = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching hub runs: ${em?.errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const openDrawer = (row?: EnergyHubClubVisit) => {
    setEditing(row);
    setDrawerOpen(true);
  };

  return (
    <PageContent>
      <Message type="info" showIcon style={{ marginBottom: 16 }}>
        {allHubs && <>Clubs request Hub Runs from their Manage tab; requests start as Requested. </>}
        A Hub Run links a club event to this venue. Once Approved, runners on that event can check in here from
        30 minutes before the start until 2 hours after the end; each check-in is charged the check-in fee.
        Approve only after the venue has agreed (e.g. on WhatsApp).
      </Message>
      <Stack className="table-toolbar" justifyContent="space-between">
        {allHubs ? <span /> : <Button appearance="primary" onClick={() => openDrawer()}>Add Hub Run</Button>}
        <Stack spacing={12}>
          <Checkbox checked={!!filterReq.upcomingOnly}
            onChange={(_, checked) => setFilterReq(pre => ({ ...pre, pageNumber: 1, upcomingOnly: checked }))}>
            Upcoming only
          </Checkbox>
          <SelectPicker
            label="Status"
            data={EnergyHubClubVisitStatusList}
            searchable={false}
            value={filterReq.status ?? null}
            onChange={val => setFilterReq(pre => ({ ...pre, pageNumber: 1, status: val ?? undefined }))}
          />
        </Stack>
      </Stack>
      <Table height={500} data={items} loading={loading}>
        <Column flexGrow={0.4}>
          <HeaderCell>Id</HeaderCell>
          <Cell dataKey="id" />
        </Column>
        {allHubs && (
          <Column flexGrow={1.3} fullText>
            <HeaderCell>Hub</HeaderCell>
            <Cell>{(row: EnergyHubClubVisit) => <Link to={`/energy-hubs/${row.energyHubId}`}>{row.energyHubName}</Link>}</Cell>
          </Column>
        )}
        <Column flexGrow={2} fullText>
          <HeaderCell>Event</HeaderCell>
          <Cell>
            {(row: EnergyHubClubVisit) => <Link to={`/running-clubs/events/${row.runningClubEventId}`}>{row.eventName}</Link>}
          </Cell>
        </Column>
        <Column flexGrow={1.3} fullText>
          <HeaderCell>Club</HeaderCell>
          <Cell>{(row: EnergyHubClubVisit) => <Link to={`/running-clubs/${row.runningClubId}`}>{row.clubName ?? `#${row.runningClubId}`}</Link>}</Cell>
        </Column>
        <Column flexGrow={1.2}>
          <HeaderCell>Start (HK)</HeaderCell>
          <Cell fullText>{(row: EnergyHubClubVisit) => jsDateToZone(row.startTime, energyHubTimeZone)}</Cell>
        </Column>
        <Column flexGrow={0.8} align="center">
          <HeaderCell>Status</HeaderCell>
          <Cell>{(row: EnergyHubClubVisit) => <EnergyHubClubVisitStatusTag status={row.status} />}</Cell>
        </Column>
        {allHubs && (
          <Column flexGrow={1.1}>
            <HeaderCell>Requested (HK)</HeaderCell>
            <Cell fullText>{(row: EnergyHubClubVisit) => jsDateToZone(row.createdAt, energyHubTimeZone)}</Cell>
          </Column>
        )}
        <Column flexGrow={0.7} align="center">
          <HeaderCell>Check-ins</HeaderCell>
          <Cell dataKey="checkInCount" />
        </Column>
        <Column flexGrow={1.5} fullText>
          <HeaderCell>Note</HeaderCell>
          <Cell dataKey="note" />
        </Column>
        <Column width={70} fixed="right">
          <HeaderCell>...</HeaderCell>
          <Cell style={{ padding: '6px' }}>
            {(row: EnergyHubClubVisit) => <IconButton icon={<EditIcon />} circle size="sm" onClick={() => openDrawer(row)} />}
          </Cell>
        </Column>
      </Table>
      <Divider />
      <TablePagination
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={val => setFilterReq(pre => ({ ...pre, pageNumber: val }))}
        onChangeLimit={val => setFilterReq(pre => ({ ...pre, pageSize: val }))}
      />
      <ClubVisitDrawer
        open={drawerOpen}
        energyHubId={energyHubId ?? editing?.energyHubId ?? 0}
        clubVisit={editing}
        onClose={() => setDrawerOpen(false)}
        onSubmitted={() => { setDrawerOpen(false); setReloadKey(k => k + 1); }}
      />
    </PageContent>
  );
};

export default EnergyHubClubVisitList;
